/**
 * DomSnapshotView — renders a ``dom`` / ``dom_dump`` artifact (full-page HTML
 * captured from Epic) inside the shared ``ArtifactLightbox``.
 *
 * Story 25240: the HTML is fetched through the signed-in /api/files route and
 * shown with ``srcDoc`` in an ``<iframe sandbox="">`` — no scripts, an opaque
 * origin, no forms — behind a ``default-src 'none'; base-uri 'none'`` meta
 * CSP so it loads nothing from the network either. Deliberately no "Open in new tab": a
 * top-level page of captured HTML on our origin would be stored XSS.
 */
import { useEffect, useState } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import { fetchStoredFileText } from "~/common/files/storedFileFetcher";
import { StoredFileLink } from "~/common/files/StoredFileLink";
import type { WorkflowArtifact } from "./WorkflowArtifactsSection";
import { formatCapturedAt } from "./WorkflowArtifactsSection";

const SNAPSHOT_CSP = `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data:; style-src 'unsafe-inline'; base-uri 'none'">`;
// `\s` also matches a byte-order mark (U+FEFF) in JavaScript.
const LEADING_DOCTYPE_RE = /^\s*<!doctype[^>]*>/i;

/** The CSP meta goes right after a leading doctype, so the snapshot keeps standards mode. */
function withSnapshotCsp(html: string): string {
  const doctype = LEADING_DOCTYPE_RE.exec(html)?.[0];
  return doctype ? doctype + SNAPSHOT_CSP + html.slice(doctype.length) : SNAPSHOT_CSP + html;
}

export function DomSnapshotView({ artifact }: { artifact: WorkflowArtifact }) {
  const [html, setHtml] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setHtml(null);
    setFailed(false);
    fetchStoredFileText(artifact.url).then(
      (text) => {
        if (!cancelled) setHtml(text);
      },
      () => {
        if (!cancelled) setFailed(true);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [artifact.url]);

  const capturedAt = formatCapturedAt(artifact.captured_at);
  const label = artifact.filename ?? artifact.step_name ?? "DOM snapshot";

  return (
    <div className="flex flex-col w-full h-full" data-testid="dom-snapshot-view">
      <div className="flex items-center justify-between gap-3 px-3 py-1.5 border-b border-primary-gray-light-200 bg-primary-gray-light-25 flex-shrink-0">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-primary-gray-light-900 truncate">{label}</p>
          {capturedAt && <p className="text-2xs text-primary-gray-light-500">{capturedAt}</p>}
        </div>
        <StoredFileLink
          url={artifact.url}
          filename={artifact.filename}
          className="text-xs text-primary-gray-light-700 hover:underline inline-flex items-center gap-1"
          data-testid="dom-snapshot-download"
        >
          <DownloadIcon fontSize="inherit" />
          Download
        </StoredFileLink>
      </div>
      <div className="flex-1 min-h-0 bg-base-white">
        {failed ? (
          <div className="flex items-center justify-center h-full p-4 text-center text-sm text-primary-gray-light-700">
            Couldn&apos;t load the DOM snapshot. Use Download to get the HTML.
          </div>
        ) : html === null ? (
          <div className="flex items-center justify-center h-full p-4 text-sm text-primary-gray-light-500">Loading…</div>
        ) : (
          <iframe
            srcDoc={withSnapshotCsp(html)}
            title={label}
            sandbox=""
            referrerPolicy="no-referrer"
            className="w-full h-full border-0"
            data-testid="dom-snapshot-iframe"
          />
        )}
      </div>
    </div>
  );
}

export default DomSnapshotView;
